import { Document, Page, View, Text, StyleSheet } from "@react-pdf/renderer";

export interface SovPdfRow {
  id: string | number;
  csiCode: string;
  description: string;
  scheduledValue: number;
  vendorName?: string;
  vendor?: string;
  status: string;
}

export interface SovPdfTradeItem {
  label: string;
  value: number;
  pct: number;
  colorHex?: string;
}

export interface SovPdfProps {
  rows: SovPdfRow[];
  contractAmount: { amount: number; currency?: { symbol: string } };
  total: number;
  variance: number;
  lineCount: number;
  divisionsCount: number;
  sovStatus: string;
  tradeBreakdown: SovPdfTradeItem[];
  lender?: { companyName?: string };
  projectName?: string;
}

const fmt = (val: number | undefined | null, sym = "$"): string => {
  const n = Number(val || 0);
  const sign = n < 0 ? "-" : "";
  return `${sign}${sym}${Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const rowStatus = (row: SovPdfRow) => {
  if (row.status === "new")
    return { label: "New", bg: "#e6f0f8", color: "#14508c" };
  if (!row.description.trim() || row.scheduledValue <= 0 || !row.vendor?.trim())
    return { label: "Incomplete", bg: "#fef9c3", color: "#b45309" };
  return { label: "Complete", bg: "#dcfce7", color: "#1a6b35" };
};

const S = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 36,
    paddingHorizontal: 30,
    fontFamily: "Helvetica",
    fontSize: 9,
    color: "#111111",
    backgroundColor: "#ffffff",
  },

  /* ── Header ── */
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#dde1e6",
  },
  headerTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 15,
    color: "#14508c",
    marginBottom: 3,
  },
  headerSub: { fontSize: 8, color: "#909399" },
  headerMetaLine: { fontSize: 8, color: "#555c66", marginBottom: 3 },
  statusPill: {
    fontSize: 7,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 4,
    alignSelf: "flex-end",
  },

  /* ── Contract strip ── */
  strip: {
    flexDirection: "row",
    backgroundColor: "#f4f5f6",
    borderRadius: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  stripCell: { flex: 1, alignItems: "center" },
  stripDivider: {
    width: 1,
    backgroundColor: "#dde1e6",
    marginVertical: 2,
  },
  stripLabel: { fontSize: 7, color: "#909399", marginBottom: 3 },
  stripValue: { fontFamily: "Helvetica-Bold", fontSize: 11, marginBottom: 2 },
  stripSub: { fontSize: 7, color: "#909399" },

  /* ── Variance banner ── */
  bannerOk: {
    flexDirection: "row",
    backgroundColor: "#f0fdf4",
    borderRadius: 4,
    paddingVertical: 7,
    paddingHorizontal: 10,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: "#1a6b35",
  },
  bannerErr: {
    flexDirection: "row",
    backgroundColor: "#fef2f2",
    borderRadius: 4,
    paddingVertical: 7,
    paddingHorizontal: 10,
    marginBottom: 12,
    borderLeftWidth: 3,
    borderLeftColor: "#b91c1c",
  },
  bannerText: { fontSize: 8, flex: 1 },
  bannerBold: { fontFamily: "Helvetica-Bold" },

  /* ── Card wrapper (matches on-screen white bordered cards) ── */
  card: {
    borderWidth: 1,
    borderColor: "#dde1e6",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 14,
    backgroundColor: "#ffffff",
  },
  cardTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#555c66",
    letterSpacing: 0.4,
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#dde1e6",
  },

  /* ── SOV Table ── */
  tHead: {
    flexDirection: "row",
    backgroundColor: "#0d3a66",
    paddingVertical: 6,
    paddingHorizontal: 6,
    borderRadius: 3,
  },
  tRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 6,
    paddingHorizontal: 6,
  },
  tRowAlt: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f0f0f0",
    paddingVertical: 6,
    paddingHorizontal: 6,
    backgroundColor: "#fafafa",
  },
  tTotal: {
    flexDirection: "row",
    backgroundColor: "#edf1f5",
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: 3,
    marginTop: 3,
  },
  th: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    color: "#ffff",
    fontWeight: "bold",
  },
  td: { fontSize: 8, color: "#111111" },
  tdMuted: { fontSize: 8, color: "#909399" },
  tdBold: { fontFamily: "Helvetica-Bold", fontSize: 9 },

  /* ── Column widths ── */
  cNum: { width: 40 },
  cCsi: { width: 50 },
  cDesc: { flex: 1 },
  cVal: { width: 88, textAlign: "right", paddingRight: 6 },
  /* gap between Scheduled Value and % of Contract */
  cPct: { width: 66, textAlign: "right", paddingRight: 12 },
  /* paddingLeft creates visible gap from % Contract column */
  cVendor: { width: 172, paddingLeft: 10 },
  cStatus: { width: 54 },

  /* ── Status badge ── */
  badge: {
    fontSize: 7,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 3,
    alignSelf: "flex-start",
  },

  /* ── Trade breakdown ── */
  tradeRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  tradeLabel: { width: 185, fontSize: 8 },
  /* bar: wider and taller to match the screen visual */
  tradeBarBg: {
    width: 340,
    height: 5,
    backgroundColor: "#edf1f5",
    borderRadius: 4,
  },
  tradeBarFill: { height: 5, borderRadius: 4 },
  tradeValue: { width: 92, textAlign: "right", fontSize: 8, paddingLeft: 10 },
  tradePct: {
    width: 48,
    textAlign: "right",
    fontSize: 8,
    color: "#909399",
    paddingLeft: 6,
  },

  /* ── Footer ── */
  footer: {
    position: "absolute",
    bottom: 16,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#dde1e6",
    paddingTop: 5,
  },
  footerText: { fontSize: 7, color: "#909399" },
});

export default function SovPdf({
  rows,
  contractAmount,
  total,
  variance,
  lineCount,
  divisionsCount,
  sovStatus,
  tradeBreakdown,
  lender,
  projectName,
}: SovPdfProps) {
  const sym = contractAmount?.currency?.symbol ?? "$";
  const isMatch = variance === 0;
  const varianceColor = isMatch
    ? "#1a6b35"
    : Math.abs(variance) < 1000
      ? "#b45309"
      : "#b91c1c";

  const statusLabel =
    sovStatus === "active" || sovStatus === "published"
      ? "Published"
      : sovStatus
        ? sovStatus.charAt(0).toUpperCase() + sovStatus.slice(1)
        : "—";
  const statusColor =
    statusLabel === "Published"
      ? "#1a6b35"
      : statusLabel === "Draft"
        ? "#b45309"
        : "#909399";

  const today = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={S.page}>
        {/* Header */}
        <View style={S.headerRow}>
          <View>
            <Text style={S.headerTitle}>SCHEDULE OF VALUES</Text>
            <Text style={S.headerSub}>
              AIA G703 — {projectName ?? "Construction Project"}
            </Text>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text style={S.headerMetaLine}>Generated: {today}</Text>
            {lender?.companyName ? (
              <Text style={S.headerMetaLine}>Lender: {lender.companyName}</Text>
            ) : null}
            <Text
              style={[
                S.statusPill,
                {
                  backgroundColor:
                    statusColor === "#1a6b35" ? "#dcfce7" : "#f4f5f6",
                  color: statusColor,
                },
              ]}
            >
              {statusLabel}
            </Text>
          </View>
        </View>

        {/* Contract Summary Strip */}
        <View style={S.strip}>
          <View style={S.stripCell}>
            <Text style={S.stripLabel}>CONTRACT AMOUNT</Text>
            <Text style={[S.stripValue, { color: "#14508c" }]}>
              {fmt(contractAmount?.amount, sym)}
            </Text>
            <Text style={S.stripSub}>From project setup</Text>
          </View>
          <View style={S.stripDivider} />
          <View style={S.stripCell}>
            <Text style={S.stripLabel}>SOV TOTAL</Text>
            <Text
              style={[S.stripValue, { color: isMatch ? "#1a6b35" : "#b91c1c" }]}
            >
              {fmt(total)}
            </Text>
            <Text style={S.stripSub}>
              {isMatch
                ? "Matches contract"
                : variance > 0
                  ? "Exceeds contract"
                  : "Below contract"}
            </Text>
          </View>
          <View style={S.stripDivider} />
          <View style={S.stripCell}>
            <Text style={S.stripLabel}>VARIANCE</Text>
            <Text style={[S.stripValue, { color: varianceColor }]}>
              {variance > 0 ? "+" : ""}
              {fmt(variance)}
            </Text>
            <Text style={S.stripSub}>
              {isMatch
                ? "No variance"
                : variance > 0
                  ? "Over contract"
                  : "Under contract"}
            </Text>
          </View>
          <View style={S.stripDivider} />
          <View style={S.stripCell}>
            <Text style={S.stripLabel}>LINE ITEMS</Text>
            <Text style={S.stripValue}>{lineCount}</Text>
            <Text style={S.stripSub}>Across {divisionsCount} divisions</Text>
          </View>
          <View style={S.stripDivider} />
          <View style={S.stripCell}>
            <Text style={S.stripLabel}>SOV STATUS</Text>
            <Text style={[S.stripValue, { color: statusColor, fontSize: 10 }]}>
              {statusLabel}
            </Text>
            <Text style={S.stripSub}>
              {sovStatus === "active" || sovStatus === "published"
                ? "Submitted"
                : "Not yet submitted"}
            </Text>
          </View>
        </View>

        {/* Variance Banner */}
        <View style={isMatch ? S.bannerOk : S.bannerErr}>
          <Text style={S.bannerText}>
            <Text style={S.bannerBold}>
              {isMatch
                ? "SOV total matches contract amount.  "
                : "SOV total does not match contract amount.  "}
            </Text>
            {isMatch
              ? `${fmt(total)} — ready to submit for CSC review.`
              : `Variance is ${variance > 0 ? "+" : ""}${fmt(variance)}. Adjust line item values before submitting.`}
          </Text>
        </View>

        {/* SOV Table */}
        <View style={S.card}>
          <Text style={S.cardTitle}>AIA G703 — SCHEDULE OF VALUES</Text>

          <View style={S.tHead}>
            <Text style={[S.th, S.cNum]}>A{"\n"}Item #</Text>
            <Text style={[S.th, S.cCsi]}>CSI{"\n"}Code</Text>
            <Text style={[S.th, S.cDesc]}>B {"\n"} Description of Work</Text>
            <Text style={[S.th, S.cVal]}>C {"\n"} Scheduled Value</Text>
            <Text style={[S.th, S.cPct]}>% of {"\n"} Contract</Text>
            <Text style={[S.th, S.cVendor]}>Subcontractor / Vendor</Text>
            <Text style={[S.th, S.cStatus]}>Status</Text>
          </View>

          {rows.map((row, i) => {
            const st = rowStatus(row);
            const pct =
              contractAmount?.amount > 0
                ? ((row.scheduledValue / contractAmount.amount) * 100).toFixed(
                    3,
                  )
                : "0.000";
            return (
              <View
                key={String(row.id)}
                style={i % 2 === 0 ? S.tRow : S.tRowAlt}
                wrap={false}
              >
                <Text style={[S.tdMuted, S.cNum]}>{i + 1}</Text>
                <Text style={[S.td, S.cCsi]}>{row.csiCode}</Text>
                <Text style={[S.td, S.cDesc]}>{row.description || "—"}</Text>
                <Text style={[S.td, S.cVal]}>{fmt(row.scheduledValue)}</Text>
                <Text style={[S.tdMuted, S.cPct]}>{pct}%</Text>
                <Text style={[S.td, S.cVendor]}>
                  {row.vendorName || row.vendor || "—"}
                </Text>
                <View style={S.cStatus}>
                  <Text
                    style={[
                      S.badge,
                      { backgroundColor: st.bg, color: st.color },
                    ]}
                  >
                    {st.label}
                  </Text>
                </View>
              </View>
            );
          })}

          {rows.length > 0 && (
            <View style={S.tTotal} wrap={false}>
              <Text style={[S.tdBold, S.cNum]} />
              <Text style={[S.tdBold, S.cCsi]} />
              <Text style={[S.tdBold, S.cDesc]}>Total Scheduled Value</Text>
              <Text style={[S.tdBold, S.cVal]}>{fmt(total)}</Text>
              <Text style={[S.tdMuted, S.cPct]}>
                {contractAmount?.amount > 0
                  ? ((total / contractAmount.amount) * 100).toFixed(3)
                  : "0.000"}
                %
              </Text>
              <Text style={[S.tdBold, S.cVendor]} />
              <Text style={[S.tdBold, S.cStatus]} />
            </View>
          )}
        </View>

        {/* Budget Breakdown by Trade */}
        {tradeBreakdown.length > 0 && (
          <View style={S.card}>
            <Text style={S.cardTitle}>BUDGET BREAKDOWN BY TRADE</Text>
            {tradeBreakdown.map((item) => (
              <View key={item.label} style={S.tradeRow} wrap={false}>
                <Text style={S.tradeLabel}>{item.label}</Text>
                <View style={S.tradeBarBg}>
                  <View
                    style={[
                      S.tradeBarFill,
                      {
                        width: Math.max(
                          1,
                          Math.min((item.pct / 100) * 340, 340),
                        ),
                        backgroundColor: item.colorHex ?? "#14508c",
                      },
                    ]}
                  />
                </View>
                <Text style={S.tradeValue}>{fmt(item.value)}</Text>
                <Text style={S.tradePct}>{item.pct}%</Text>
              </View>
            ))}
          </View>
        )}

        {/* Footer (fixed on every page) */}
        <View style={S.footer} fixed>
          <Text style={S.footerText}>Schedule of Values · {today}</Text>
          <Text
            style={S.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}
